import { useCallback, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import { compose } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import Form from '@gef-ui/form-material/Form';

import { ComponentLoader } from 'components';
import Panel from 'components/atoms/GefMui/Panel';
import codelistProvider from 'components/decorators/codelistProvider';
import { codelists } from 'constants/codelists';
import MODALS from 'constants/modals';
import PAGES from 'constants/pages';
import { KCL_DOCUMENT_CHECK_ADMINS, KCL_DOCUMENT_CHECK_MARPO } from 'features/common/acl/constants';
import { urls } from 'features/common/fetch/constants';
import { reload as reloadDocumentsToReview } from 'features/views/productionControl/documentsToReview/actions';
import formDescriptorFnDocumentsToReview, {
	DOCUMENTS_TO_REVIEW_FORM_NAME,
} from 'features/views/productionControl/documentsToReview/form';
import { reload as reloadMyDocuments } from 'features/views/productionControl/myDocuments/actions';
import formDescriptorFnMyDocuments, { MY_DOCUMENTS_FORM_NAME } from 'features/views/productionControl/myDocuments/form';
import { reportKopr } from 'features/views/productionControl/reportProductionControl/actions';
import { fetchDocumentCheckWaveCodelist } from 'features/views/productionControl/selectionCriteria/actions';
import formDescriptorFnSelection, {
	SELECTION_CRITERIA_FORM_NAME,
} from 'features/views/productionControl/selectionCriteria/form';
import {
	getShowMyDocuments,
	getUserIsSuperior,
	getUserIsSuperiorAndSubordinate,
} from 'features/views/productionControl/selectors';

import DocumentsToReview from './components/DocumentsToReview';
import MyDocuments from './components/MyDocuments';
import SelectionCriteria from './components/SelectionCriteria';
import ReportProductionControlModal from './modals/ReportProductionControlModal';

export const ProductionControl = () => {
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(fetchDocumentCheckWaveCodelist());
	}, [dispatch]);

	const userIsCentralOrMarpo = useSelector((state) =>
		hasPermissionByIds(state)([...KCL_DOCUMENT_CHECK_ADMINS, ...KCL_DOCUMENT_CHECK_MARPO]),
	);

	const userIsSuperior = useSelector(getUserIsSuperior);
	const userIsSuperiorAndSubordinate = useSelector(getUserIsSuperiorAndSubordinate);
	const showMyDocuments = useSelector(getShowMyDocuments);

	const handleReloadMyDocuments = useCallback(() => {
		dispatch(reloadMyDocuments());
	}, [dispatch]);

	const handleReloadDocumentsToReview = useCallback(() => {
		dispatch(reloadDocumentsToReview());
	}, [dispatch]);

	const handleReport = useCallback(() => {
		dispatch(reportKopr());
	}, [dispatch]);

	return (
		<Panel spacing={5} screenId={PAGES.productionControl.id} screenName={PAGES.productionControl.name}>
			<Form name={SELECTION_CRITERIA_FORM_NAME} formDescriptorFn={formDescriptorFnSelection}>
				<SelectionCriteria showMyDocuments={showMyDocuments} onReportConfirm={handleReport} />
			</Form>
			<ComponentLoader fetchStorePath={urls.koprDocumentsToReview} onReload={handleReloadMyDocuments}>
				<ComponentLoader fetchStorePath={urls.koprMyDocuments} onReload={handleReloadDocumentsToReview}>
					<Stack spacing={5}>
						<Form name={MY_DOCUMENTS_FORM_NAME} formDescriptorFn={formDescriptorFnMyDocuments}>
							{!userIsCentralOrMarpo && (!userIsSuperior || userIsSuperiorAndSubordinate) && (
								<MyDocuments showMyDocuments={showMyDocuments} />
							)}
						</Form>
						<Form name={DOCUMENTS_TO_REVIEW_FORM_NAME} formDescriptorFn={formDescriptorFnDocumentsToReview}>
							{(userIsCentralOrMarpo || userIsSuperior) && <DocumentsToReview />}
						</Form>
					</Stack>
				</ComponentLoader>
			</ComponentLoader>
			<ReportProductionControlModal id={MODALS.productionControl_report} onConfirm={handleReport} />
		</Panel>
	);
};

export default compose(
	codelistProvider(codelists.documentType),
	codelistProvider(codelists.documentState),
	codelistProvider(codelists.documentCheckFinalStateAdmin),
)(ProductionControl);
